import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { MatChipEditedEvent, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClipboardService } from 'ngx-clipboard';
import { Tenant } from 'src/app/models/tenant/tenant';
import { InstancesService } from 'src/app/services/api/instances.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DateTimeFormatService } from 'src/app/services/util/date-time-format.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { ToastrErrorService } from 'src/app/services/util/toastr-error.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { translate } from '@jsverse/transloco';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { Permissions } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';

const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.TENANT_UPDATE,
    action: Permissions.TENANT_UPDATE
  },
  {
    key: PermissionKeys.TENANT_READ,
    action: Permissions.TENANT_READ
  }
];

@Component({
    selector: 'app-tenant-settings',
    templateUrl: './tenant-settings.component.html',
    styleUrls: ['./tenant-settings.component.scss'],
    standalone: false
})


export class TenantSettingsComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);

  Permissions = Permissions;
  PermissionKeys = PermissionKeys;
  permissionsMap: { [key: string]: boolean };
  announcer = inject(LiveAnnouncer);
  isEdit: boolean = true;
  tenantId: any;
  tenantData: Tenant;
  emailList: any[];
  limit = 10;
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA, SPACE] as const;
  tenantForm: FormGroup;
  invalidEmail: any = [];
  initialLoader: boolean = true;

  constructor(private fb: FormBuilder, private activatedRoute: ActivatedRoute,
     private tenantService: TenantService, private router: Router,
    public dateTimeFormatService: DateTimeFormatService,private permissionsService: PermissionsService, private toastrService: CustomToastrService) {
    
  }

  ngOnInit(): void {
    this.tenantId = this.activatedRoute.snapshot.params.id;
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    if (!this.permissionsMap[PermissionKeys.TENANT_READ]) {
      this.router.navigate(['/', this.tenantId, 'tenant', 'permission-unauthorized'], {skipLocationChange: true});
    }
    this.tenantService.getTenantDetails(this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.tenantData = data;
        this.emailList = data?.notificationEmails ? data?.notificationEmails : [];
        this.initialLoader = false;
      });
    let formConfig: any;
    formConfig = {
      tenantId: [''],
      notificationEmails: ['', [Validators.required]]
    };
    this.tenantForm = this.fb.group(formConfig);
    this.tenantForm.get('tenantId').setValue(this.tenantId);

  }


  remove(email): void {
    const index = this.emailList.indexOf(email);
    if (index >= 0) {
      this.emailList.splice(index, 1);
    }

    if (this.invalidEmail.includes(email)) {
      const emailIndex = this.invalidEmail.indexOf(email);
      this.invalidEmail.splice(emailIndex, 1);
    }
  }

  editEmail(): void {
    this.tenantForm.get('notificationEmails').setValue('');
    this.isEdit = !this.isEdit
  }

  save(): void {
    let emailList: any = this.emailList;
    this.tenantData.notificationEmails = this.emailList;
    this.initialLoader = true;
    this.tenantService.updateTenant(this.tenantData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.initialLoader = false;
        this.isEdit = !this.isEdit;
        setTimeout(() => {
			        const title = translate('TENANT_LAYOUT.MENU.SETTINGS');
              const message = translate('TENANT_LAYOUT.MENU.EMAIL_ID_ADDED');
              this.toastrService.successMessage(message, title);
        }, 500);
      }, () => {
        this.tenantForm.get('notificationEmails').setValue('');
        this.initialLoader = false;
      });
  }

  edit(email: any, event: MatChipEditedEvent) {
    const value: any = event.value.trim();

    if (!value) {
      this.remove(email);
      return;
    }

    const index = this.emailList.indexOf(email);
    const emails = this.emailList.map(item => item.toLowerCase());
    const findDuplicate = emails.find(item => item === value.toLowerCase());
    if (index >= 0 && !findDuplicate) {
      this.emailList[index] = value;
    }
    this.invalidEmail = [];
    this.isValidEmail(value);
  }

  add(event: MatChipInputEvent): void {
    const value: any = (event.value || '').trim();

    if (value) {
      if (this.emailList?.length <= this.limit) {
        const emails = this.emailList.map(item => item.toLowerCase());
        const findDuplicate = emails.find(item => item === value.toLowerCase());
        if (!findDuplicate) {
          this.emailList.push(value);
        }
      }
    }
    event.chipInput!.clear();

  }


  isValidEmail(email): boolean {
    const validateEmail = /^[a-zA-Z0-9._%+-]+@[A-Za-z0-9.-]+\.[a-z]{2,4}$/;
    const splitEmail = email.split('@');
    if (validateEmail.test(email) && splitEmail[1].toLowerCase() === 'temenos.com') {
      return true;
    } else {
      const emails = this.invalidEmail?.map(item => item.toLowerCase());
      const findDuplicate = emails.find(item => item === email.toLowerCase());
      if (!findDuplicate) {
        this.invalidEmail.push(email);
      }
    }

  }


  cancel(): void {
    this.isEdit = true;
    this.invalidEmail = [];
    this.initialLoader = true;

    this.ngOnInit();

  }



}
